import { AlertTriangle, PauseCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { useAccountStanding } from "@/contexts/AccountStandingContext";
import { canResolveStandingAction } from "@/lib/billing-payer";
import { ACCOUNT_RESOLVE_PATH } from "@/lib/account-resolve";
import { ASK_ADMIN_MESSAGE, NOTIFY_ADMIN_LABEL, REAUTHORIZE_SUBJECT, standingBannerCopy } from "@/lib/account-standing-copy";
import { cn } from "@/lib/utils";

export interface AccountStandingBannerProps {
  isSuperAdmin: boolean;
  unansweredPath?: string | null;
}

export const UNANSWERED_LINK_LABEL = "Ver conversaciones sin respuesta";

export function AccountStandingBanner({ isSuperAdmin, unansweredPath = null }: AccountStandingBannerProps) {
  const standing = useAccountStanding();
  const { userRole } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  if (!standing || (standing.kind !== "grace" && standing.kind !== "paused")) return null;
  const copy = standingBannerCopy(standing);
  if (!copy) return null;

  const action = standing.action;
  const canResolve = canResolveStandingAction(action, userRole, isSuperAdmin);
  const paused = copy.tone === "paused";
  const Icon = paused ? PauseCircle : AlertTriangle;
  const buttonClass = cn(
    "shrink-0 inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
    paused
      ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
      : "bg-amber-800 text-white hover:bg-amber-900",
  );

  return (
    <div
      role={paused ? "alert" : "status"}
      data-testid="account-standing-banner"
      data-standing={standing.kind}
      className={cn(
        "shadow-panel mx-2 mt-2 md:mx-3 md:mt-3 rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm",
        paused ? "bg-destructive/10" : "bg-amber-500/15",
      )}
    >
      <div className="flex min-w-0 flex-1 basis-64 items-start gap-2">
        <Icon className={cn("h-4 w-4 shrink-0 mt-0.5", paused ? "text-destructive" : "text-amber-700 dark:text-amber-400")} aria-hidden="true" />
        <p className={cn("min-w-0 break-words font-medium", paused ? "text-destructive" : "text-amber-900 dark:text-amber-200")}>
          {copy.message}
          {!canResolve && <> {ASK_ADMIN_MESSAGE}</>}
        </p>
      </div>
      {paused && unansweredPath && (
        <button
          type="button"
          onClick={() => navigate(unansweredPath)}
          className="shrink-0 text-xs font-medium text-destructive underline underline-offset-2 hover:no-underline"
        >
          {UNANSWERED_LINK_LABEL}
        </button>
      )}
      {canResolve && action === "support" && (
        <a
          href={`mailto:${brand.supportEmail}?subject=${encodeURIComponent(REAUTHORIZE_SUBJECT)}`}
          className={buttonClass}
        >
          {copy.actionLabel}
        </a>
      )}
      {canResolve && action !== "support" && (
        <button type="button" onClick={() => navigate(ACCOUNT_RESOLVE_PATH)} className={buttonClass}>
          {copy.actionLabel}
        </button>
      )}
      {!canResolve && (
        <button
          type="button"
          onClick={() => navigate(ACCOUNT_RESOLVE_PATH)}
          className={cn(
            "shrink-0 text-xs font-medium underline underline-offset-2 hover:no-underline",
            paused ? "text-destructive" : "text-amber-900 dark:text-amber-200",
          )}
        >
          {NOTIFY_ADMIN_LABEL}
        </button>
      )}
    </div>
  );
}
